/**
 * QuotientFamilialEncart — espace famille : affiche le QF connu + permet de le
 * récupérer/mettre à jour via FranceConnect (identité pivot → API Particulier).
 *
 * En mode mock (flags OFF), le bouton FranceConnect renvoie vers un écran de
 * simulation ; en réel, il redirige vers la mire FranceConnect. Le QF revient
 * automatiquement après le callback (paramètre ?qf= dans l'URL de retour).
 */

import { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, ChevronDown } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Badge, Alert } from '@/components/ui';
import type { IntegrationsStatut } from '@roronoa-collectivites/shared-types';

export function QuotientFamilialEncart({ familleId, quotientFamilial, qfSource }: {
  familleId: string;
  quotientFamilial: number | null;
  qfSource?: string | null;
}) {
  const [integrations, setIntegrations] = useState<IntegrationsStatut | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okQf, setOkQf] = useState<number | null>(null);
  const [retourErreurQf, setRetourErreurQf] = useState<string | null>(null);
  const [open, setOpen] = useState(false); // replié par défaut

  useEffect(() => {
    // Affiche un message si on revient d'un callback FranceConnect avec un QF,
    // OU une erreur explicite si la récupération du QF a échoué (qfErreur).
    const params = new URLSearchParams(window.location.search);
    if (params.get('fc') === 'ok' && params.get('qf')) {
      setOkQf(Number(params.get('qf')));
      setOpen(true); // déplie pour montrer le message de succès au retour FranceConnect
    } else if (params.get('fc') === 'ok' && params.get('qfErreur')) {
      // Traduction des codes d'erreur les plus courants en message lisible.
      const code = params.get('qfErreur') ?? '';
      const lisible =
        code === 'QF_DOSSIER_ABSENT'
          ? "Aucun dossier allocataire CAF/MSA n'a été trouvé pour cette identité (c'est le cas de la plupart des identités FranceConnect de test). Le quotient familial n'a pas pu être récupéré."
          : code === 'API_PARTICULIER_TOKEN_COMMUNE_ABSENT'
          ? "Le jeton API Particulier de la commune n'est pas configuré (fiche mairie)."
          : code === 'RECIPIENT_ABSENT'
          ? "Le SIRET de la commune (recipient) n'est pas renseigné dans la fiche mairie."
          : `La récupération du quotient familial a échoué (${code}).`;
      setRetourErreurQf(lisible);
      setOpen(true);
    }
    (async () => {
      try { setIntegrations(await api.scolaire.facturation.integrationsStatut()); } catch { /* non bloquant */ }
    })();
  }, []);

  async function connecterFranceConnect() {
    setBusy(true); setError(null);
    try {
      const { url } = await api.scolaire.facturation.franceConnectLogin(familleId);
      window.location.href = url; // redirection vers FranceConnect (ou écran mock)
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur');
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4">
      <CardBody>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
        >
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-ink-muted transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          <ShieldCheck className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
          <h2 className="font-display text-base font-semibold text-ink">Quotient familial</h2>
          {!open && quotientFamilial != null && (
            <Badge variant="info" className="ml-1">{quotientFamilial}</Badge>
          )}
        </button>

        {open && (
          <div className="mt-3">
            {error && <Alert variant="error">{error}</Alert>}
            {okQf != null && <Alert variant="success">Quotient familial mis à jour : {okQf}.</Alert>}
            {retourErreurQf && <Alert variant="error">{retourErreurQf}</Alert>}

            <div className="flex flex-wrap items-center gap-3">
              <div className="text-sm text-ink-muted">
                Valeur connue :{' '}
                {quotientFamilial != null
                  ? <span className="font-semibold text-ink">{quotientFamilial}</span>
                  : <span className="italic">non renseigné</span>}
                {qfSource && <Badge variant="info" className="ml-2">{qfSource === 'api_particulier' ? 'CAF/MSA' : 'saisi'}</Badge>}
              </div>
              <Button variant="cantine" size="sm" loading={busy} onClick={() => void connecterFranceConnect()}>
                <RefreshCw className="h-4 w-4" /> Récupérer via FranceConnect
              </Button>
            </div>
            <p className="mt-2 text-2xs text-ink-subtle">
              La connexion FranceConnect permet de récupérer automatiquement votre quotient
              familial auprès de la CAF/MSA, sans pièce justificative.
              {integrations && !integrations.franceConnect && ' (mode démonstration : connexion simulée)'}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
